import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { progressService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import { Badge, Button, Card, Input, Textarea } from '../components/ui';

const ProgressView = () => {
  const { user } = useAuth();
  const [trends, setTrends] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [assessmentForm, setAssessmentForm] = useState({
    emotionalRegulation: 5,
    physicalHealth: 5,
    relationshipQuality: 5,
    cravingIntensity: 5,
    lifeSatisfaction: 5,
    notes: ''
  });
  const [assessmentMessage, setAssessmentMessage] = useState(null);
  const [assessmentSaving, setAssessmentSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const getCurrentMonth = useCallback(
    () => Math.min(12, Math.ceil(((user?.currentDay || 1) / 30))),
    [user?.currentDay]
  );

  const loadProgressData = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const [trendsResponse, progressResponse] = await Promise.all([
        progressService.getTrends(user.id, 30),
        progressService.getProgress(user.id)
      ]);

      setTrends(trendsResponse.data.data.trends);
      setAssessments(progressResponse.data.data.progress?.monthlyAssessments || []);
    } catch (error) {
      console.error('Failed to load trends:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProgressData();
  }, [loadProgressData]);

  useEffect(() => {
    const currentMonth = getCurrentMonth();
    const existing = assessments.find(assessment => assessment.month === currentMonth);

    if (existing) {
      setAssessmentForm({
        emotionalRegulation: existing.domains?.emotionalRegulation ?? 5,
        physicalHealth: existing.domains?.physicalHealth ?? 5,
        relationshipQuality: existing.domains?.relationshipQuality ?? 5,
        cravingIntensity: existing.domains?.cravingIntensity ?? 5,
        lifeSatisfaction: existing.domains?.lifeSatisfaction ?? 5,
        notes: existing.notes || ''
      });
    }
  }, [assessments, getCurrentMonth]);

  const handleAssessmentChange = (field, value) => {
    setAssessmentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const normalizeScore = (rawValue) => {
    const parsed = Number.parseInt(rawValue, 10);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(1, Math.min(10, parsed));
  };

  const buildAssessmentPayload = (currentMonth) => {
    const normalizedDomains = {
      emotionalRegulation: normalizeScore(assessmentForm.emotionalRegulation),
      physicalHealth: normalizeScore(assessmentForm.physicalHealth),
      relationshipQuality: normalizeScore(assessmentForm.relationshipQuality),
      cravingIntensity: normalizeScore(assessmentForm.cravingIntensity),
      lifeSatisfaction: normalizeScore(assessmentForm.lifeSatisfaction)
    };

    const invalidField = Object.entries(normalizedDomains).find(([, score]) => score === null);
    if (invalidField) {
      return {
        error: 'Each assessment score must be a whole number between 1 and 10.'
      };
    }

    const trimmedNotes = (assessmentForm.notes || '').trim();
    if (trimmedNotes.length > 1000) {
      return {
        error: 'Notes must be 1000 characters or fewer.'
      };
    }

    if (!Number.isInteger(currentMonth) || currentMonth < 1 || currentMonth > 12) {
      return {
        error: 'Unable to determine current month for assessment. Please refresh and try again.'
      };
    }

    return {
      payload: {
        month: currentMonth,
        domains: normalizedDomains,
        notes: trimmedNotes
      }
    };
  };

  const handleAssessmentSubmit = async () => {
    if (!user?.id) return;
    const currentMonth = getCurrentMonth();

    setAssessmentSaving(true);
    setAssessmentMessage(null);

    const { payload, error: payloadError } = buildAssessmentPayload(currentMonth);
    if (payloadError) {
      setAssessmentMessage({ type: 'error', text: payloadError });
      setAssessmentSaving(false);
      return;
    }

    try {

      const response = await progressService.addAssessment(user.id, payload);
      const savedAssessment = response.data.data.assessment;

      setAssessments(prev => {
        const next = prev.filter(assessment => assessment.month !== savedAssessment.month);
        return [...next, savedAssessment].sort((a, b) => a.month - b.month);
      });
      setAssessmentMessage({ type: 'success', text: 'Monthly assessment saved.' });
    } catch (error) {
      console.error('Failed to save monthly assessment:', error);
      const validationError = error.response?.data?.errors?.[0]?.message;
      setAssessmentMessage({
        type: 'error',
        text: validationError || error.response?.data?.message || 'Failed to save monthly assessment.'
      });
    } finally {
      setAssessmentSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading your progress..." />;
  }

  const currentMonth = getCurrentMonth();
  const sortedAssessments = [...assessments].sort((a, b) => a.month - b.month);
  const latestAssessment = sortedAssessments[sortedAssessments.length - 1] || null;
  const previousAssessment = sortedAssessments[sortedAssessments.length - 2] || null;
  const isAssessmentDue = !latestAssessment || latestAssessment.month !== currentMonth;
  const showComparison = latestAssessment && previousAssessment;

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <Badge variant="primary" size="sm">Progress Snapshot</Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            Your Progress & Trends
          </h2>
          <p className="mt-2 text-neutral-600">
            Track your mood, challenges, and sleep over time.
          </p>
        </div>

        <Card className="mb-6" colorVariant="cyan" borderAccent="cyan">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-display font-semibold text-cyan-900">
                Monthly Milestone Assessment
              </h3>
              <p className="mt-1 text-sm text-cyan-700">
                Month {currentMonth} of your program. Track five mental wellness domains.
              </p>
            </div>
            <Badge variant={isAssessmentDue ? 'warning' : 'success'} size="sm">
              {isAssessmentDue ? 'Due now' : 'Completed'}
            </Badge>
          </div>

          {assessmentMessage && (
            <div
              className={`mt-4 rounded-soft border p-3 text-sm ${
                assessmentMessage.type === 'success'
                  ? 'border-success-200 bg-success-50 text-success-700'
                  : 'border-error-200 bg-error-50 text-error-700'
              }`}
            >
              {assessmentMessage.text}
            </div>
          )}

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Input
              label="Emotional regulation (1-10)"
              type="number"
              min={1}
              max={10}
              value={assessmentForm.emotionalRegulation}
              onChange={(e) => handleAssessmentChange('emotionalRegulation', e.target.value)}
              fullWidth
            />
            <Input
              label="Physical health (1-10)"
              type="number"
              min={1}
              max={10}
              value={assessmentForm.physicalHealth}
              onChange={(e) => handleAssessmentChange('physicalHealth', e.target.value)}
              fullWidth
            />
            <Input
              label="Relationship quality (1-10)"
              type="number"
              min={1}
              max={10}
              value={assessmentForm.relationshipQuality}
              onChange={(e) => handleAssessmentChange('relationshipQuality', e.target.value)}
              fullWidth
            />
            <Input
              label="Challenge intensity (1-10)"
              type="number"
              min={1}
              max={10}
              value={assessmentForm.cravingIntensity}
              onChange={(e) => handleAssessmentChange('cravingIntensity', e.target.value)}
              fullWidth
            />
            <Input
              label="Life satisfaction (1-10)"
              type="number"
              min={1}
              max={10}
              value={assessmentForm.lifeSatisfaction}
              onChange={(e) => handleAssessmentChange('lifeSatisfaction', e.target.value)}
              fullWidth
            />
            <Textarea
              label="Notes"
              rows={4}
              value={assessmentForm.notes}
              onChange={(e) => handleAssessmentChange('notes', e.target.value)}
              placeholder="Optional reflections for this month."
              fullWidth
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button variant="cyan" onClick={handleAssessmentSubmit} disabled={assessmentSaving}>
              {assessmentSaving ? 'Saving...' : 'Save Assessment'}
            </Button>
            {latestAssessment && (
              <p className="text-xs text-neutral-500">
                Last saved: {new Date(latestAssessment.date).toLocaleDateString()}
              </p>
            )}
          </div>

          {showComparison && (
            <div className="mt-6 rounded-soft border border-neutral-200 bg-neutral-50 p-4">
              <p className="text-sm font-semibold text-neutral-700">Compared to last month</p>
              <div className="mt-3 grid gap-3 md:grid-cols-5">
                {[
                  ['Emotional', 'emotionalRegulation'],
                  ['Physical', 'physicalHealth'],
                  ['Relationship', 'relationshipQuality'],
                  ['Challenge', 'cravingIntensity'],
                  ['Life', 'lifeSatisfaction']
                ].map(([label, key]) => {
                  const current = latestAssessment.domains?.[key] ?? 0;
                  const previous = previousAssessment.domains?.[key] ?? 0;
                  const delta = current - previous;

                  return (
                    <div key={key} className="rounded-soft border border-neutral-200 bg-white p-3">
                      <p className="text-xs text-neutral-500">{label}</p>
                      <p className="mt-1 text-lg font-semibold text-neutral-800">{current}</p>
                      <p className={`text-xs ${delta >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                        {delta >= 0 ? '+' : ''}{delta}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>

        <Card className="mb-6" colorVariant="cyan" borderAccent="cyan">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-display font-semibold text-cyan-900">30-Day Trends</h3>
            <Badge variant="secondary">Last 30 days</Badge>
          </div>
          <div className="mt-4 h-[360px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" label={{ value: 'Day', position: 'insideBottom', offset: -5 }} />
                <YAxis domain={[0, 10]} label={{ value: 'Scale (1-10)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="mood" stroke="#2A8C8E" name="Mood" strokeWidth={2} />
                <Line type="monotone" dataKey="craving" stroke="#E6A07C" name="Challenge" strokeWidth={2} />
                <Line type="monotone" dataKey="sleep" stroke="#6ED3A8" name="Sleep" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          <Card colorVariant="purple" borderAccent="purple">
            <p className="text-sm text-purple-600 font-medium">Average Mood</p>
            <p className="mt-2 text-3xl font-bold text-purple-900">
              {trends.length > 0
                ? (trends.reduce((sum, t) => sum + t.mood, 0) / trends.length).toFixed(1)
                : 'N/A'}
            </p>
          </Card>

          <Card colorVariant="coral" borderAccent="coral">
            <p className="text-sm text-coral-600 font-medium">Average Challenge</p>
            <p className="mt-2 text-3xl font-bold text-coral-900">
              {trends.length > 0
                ? (trends.reduce((sum, t) => sum + t.craving, 0) / trends.length).toFixed(1)
                : 'N/A'}
            </p>
          </Card>

          <Card colorVariant="emerald" borderAccent="emerald">
            <p className="text-sm text-emerald-600 font-medium">Average Sleep</p>
            <p className="mt-2 text-3xl font-bold text-emerald-900">
              {trends.length > 0
                ? (trends.reduce((sum, t) => sum + t.sleep, 0) / trends.length).toFixed(1)
                : 'N/A'}
            </p>
            <p className="text-xs text-emerald-600">hours</p>
          </Card>
        </div>
      </main>
    </>
  );
};

export default ProgressView;
